import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, HelpCircle } from "lucide-react";
import type { TelegramUser } from "../types";
import { useMobile } from "../hooks/useMobile";
import { getTelegramPhotoUrl } from "../constants";
import { LAYERS } from "../constants/layers";

interface HeaderProps {
  user: TelegramUser | null;
}

export const Header = ({ user }: HeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [practiceOpen, setPracticeOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const practiceRef = useRef<HTMLDivElement>(null);
  const toolsRef = useRef<HTMLDivElement>(null);
  const practiceButtonRef = useRef<HTMLButtonElement>(null);
  const toolsButtonRef = useRef<HTMLButtonElement>(null);
  const practiceMenuRef = useRef<HTMLDivElement>(null);
  const toolsMenuRef = useRef<HTMLDivElement>(null);
  const [practiceMenuPos, setPracticeMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [toolsMenuPos, setToolsMenuPos] = useState<{ top: number; left: number } | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toolsCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePracticeEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setPracticeOpen(true);
  };

  const handlePracticeLeave = () => {
    closeTimerRef.current = setTimeout(() => setPracticeOpen(false), 150);
  };

  const handleToolsEnter = () => {
    if (toolsCloseTimerRef.current) {
      clearTimeout(toolsCloseTimerRef.current);
      toolsCloseTimerRef.current = null;
    }
    setToolsOpen(true);
  };

  const handleToolsLeave = () => {
    toolsCloseTimerRef.current = setTimeout(() => setToolsOpen(false), 150);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (toolsCloseTimerRef.current) clearTimeout(toolsCloseTimerRef.current);
    };
  }, []);

  useEffect(() => { setPhotoError(false); }, [user?.photo_url]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        practiceRef.current?.contains(t) ||
        practiceMenuRef.current?.contains(t)
      ) {
        /* stay open */
      } else {
        setPracticeOpen(false);
      }
      if (toolsRef.current?.contains(t) || toolsMenuRef.current?.contains(t)) {
        /* stay open */
      } else {
        setToolsOpen(false);
      }
    };
    if (practiceOpen || toolsOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [practiceOpen, toolsOpen]);

  const updatePracticeMenuPos = () => {
    const el = practiceButtonRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPracticeMenuPos({ top: r.bottom + 8, left: r.left });
  };

  const updateToolsMenuPos = () => {
    const el = toolsButtonRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setToolsMenuPos({ top: r.bottom + 8, left: r.left });
  };

  useLayoutEffect(() => {
    if (!practiceOpen) {
      setPracticeMenuPos(null);
      return;
    }
    updatePracticeMenuPos();
    window.addEventListener("scroll", updatePracticeMenuPos, true);
    window.addEventListener("resize", updatePracticeMenuPos);
    return () => {
      window.removeEventListener("scroll", updatePracticeMenuPos, true);
      window.removeEventListener("resize", updatePracticeMenuPos);
    };
  }, [practiceOpen]);

  useLayoutEffect(() => {
    if (!toolsOpen) {
      setToolsMenuPos(null);
      return;
    }
    updateToolsMenuPos();
    window.addEventListener("scroll", updateToolsMenuPos, true);
    window.addEventListener("resize", updateToolsMenuPos);
    return () => {
      window.removeEventListener("scroll", updateToolsMenuPos, true);
      window.removeEventListener("resize", updateToolsMenuPos);
    };
  }, [toolsOpen]);

  useEffect(() => { setPracticeOpen(false); setToolsOpen(false); }, [location.pathname]);

  const isSimulatorActive = location.pathname === "/simulator" || location.pathname.startsWith("/task/");
  const isSandboxActive = location.pathname === "/sandbox";
  const isDocsActive = location.pathname === "/docs";
  const isAITutorActive = location.pathname === "/ai-tutor";
  const isJobMarketActive = location.pathname === "/job-market";
  const isCoursesActive = location.pathname === "/courses" || location.pathname.startsWith("/courses/");
  const isFAQActive = location.pathname === "/faq" || location.pathname.startsWith("/faq/");
  const isPracticeActive = isSimulatorActive || isDocsActive;
  const isToolsActive = isSandboxActive || isAITutorActive || isJobMarketActive;

  const loginButton = (
    <Link
      to="/login"
      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition text-sm font-medium shadow-sm"
    >
      Войти
    </Link>
  );

  return (
    <header className="bg-neutral-bg border-b border-neutral-border fixed top-0 left-0 right-0 z-50 shadow-sm h-[var(--header-height,4.5rem)] min-h-[var(--header-height,4.5rem)]">
      <div className="w-full h-full px-6 flex justify-between items-center">

        {/* Logo */}
        <Link to="/" className="flex items-center flex-shrink-0">
          <span className="text-xl font-heading font-bold text-neutral-text">
            Quantile <span className="text-primary">Camp</span>
          </span>
        </Link>

        {/* Center nav (desktop) */}
        {!isMobile && (
          <nav className="flex items-center gap-1">
            {/* Курсы — прямая ссылка */}
            <Link
              to="/courses"
              className={`text-sm px-3 py-1.5 rounded-lg hover:bg-primary/10 hover:text-primary transition font-medium ${
                isCoursesActive ? "bg-primary/10 text-primary" : "text-neutral-text"
              }`}
            >
              Курсы
            </Link>

            {/* Практика — dropdown: Тренажёр + Схемы данных */}
            <div className="relative" ref={practiceRef}>
              <button
                ref={practiceButtonRef}
                onMouseEnter={handlePracticeEnter}
                onMouseLeave={handlePracticeLeave}
                onClick={() => setPracticeOpen((v) => !v)}
                className={`text-sm px-3 py-1.5 rounded-lg hover:bg-primary/10 hover:text-primary transition font-medium flex items-center gap-1.5 ${
                  isPracticeActive ? "bg-primary/10 text-primary" : "text-neutral-text"
                }`}
              >
                Практика
                <ChevronDown
                  size={15}
                  className={`transition-transform duration-200 ${practiceOpen ? "rotate-180" : ""}`}
                />
              </button>
            </div>

            {/* Инструменты — dropdown: Sandbox + AI Tutor */}
            <div className="relative" ref={toolsRef}>
              <button
                ref={toolsButtonRef}
                onMouseEnter={handleToolsEnter}
                onMouseLeave={handleToolsLeave}
                onClick={() => setToolsOpen((v) => !v)}
                className={`text-sm px-3 py-1.5 rounded-lg hover:bg-primary/10 hover:text-primary transition font-medium flex items-center gap-1.5 ${
                  isToolsActive ? "bg-primary/10 text-primary" : "text-neutral-text"
                }`}
              >
                Инструменты
                <ChevronDown
                  size={15}
                  className={`transition-transform duration-200 ${toolsOpen ? "rotate-180" : ""}`}
                />
              </button>
            </div>
          </nav>
        )}

        {/* Right: utility zone (desktop) + burger (mobile) */}
        <div className="flex items-center gap-2">
          {!isMobile && (
            <Link
              to="/faq"
              className={`text-sm px-3 py-1.5 rounded-lg hover:bg-primary/10 hover:text-primary transition font-medium flex items-center gap-1.5 ${
                isFAQActive ? "bg-primary/10 text-primary" : "text-neutral-text"
              }`}
            >
              <HelpCircle size={15} />
              FAQ
            </Link>
          )}

          {isMobile && (
            <button
              className="p-2 rounded-lg border border-neutral-border bg-white hover:bg-primary/10 transition-colors"
              onClick={() => setMenuOpen(true)}
              aria-label="Открыть меню"
            >
              <span className="block w-5 h-0.5 bg-neutral-text mb-1" />
              <span className="block w-5 h-0.5 bg-neutral-text mb-1" />
              <span className="block w-5 h-0.5 bg-neutral-text" />
            </button>
          )}

          {user ? (
            <button onClick={() => navigate("/profile")} className="flex items-center gap-2">
              {(() => {
                const photoUrl = getTelegramPhotoUrl(user.photo_url);
                return photoUrl && !photoError ? (
                  <img
                    src={photoUrl}
                    alt={`Фото профиля ${user.first_name}`}
                    className="w-8 h-8 rounded-full border border-neutral-border"
                    width="32"
                    height="32"
                    loading="lazy"
                    decoding="async"
                    onError={() => setPhotoError(true)}
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-sm font-semibold">
                    {user.first_name?.charAt(0).toUpperCase()}
                  </div>
                );
              })()}
              {!isMobile && (
                <span className="text-neutral-text text-sm font-medium hover:text-primary transition cursor-pointer">
                  {user.first_name}
                </span>
              )}
            </button>
          ) : (
            loginButton
          )}
        </div>
      </div>

      {/* Desktop: меню «Практика» / «Инструменты» в портале — z-index выше панели AI Tutor (LAYERS.aiTutorPanel), иначе чат перекрывает дропдауны */}
      {!isMobile &&
        practiceOpen &&
        practiceMenuPos &&
        createPortal(
          <div
            ref={practiceMenuRef}
            className="fixed w-80 bg-white border border-neutral-border rounded-xl shadow-2xl overflow-hidden"
            style={{ top: practiceMenuPos.top, left: practiceMenuPos.left, zIndex: LAYERS.headerDropdown }}
            onMouseEnter={handlePracticeEnter}
            onMouseLeave={handlePracticeLeave}
          >
            <div className="p-3 space-y-0.5">
              <Link
                to="/simulator"
                className={`group flex items-start gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isSimulatorActive ? "bg-primary/10" : "hover:bg-neutral-light/80"
                }`}
                onClick={() => setPracticeOpen(false)}
              >
                <div
                  className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                    isSimulatorActive
                      ? "bg-primary text-white"
                      : "bg-neutral-light group-hover:bg-primary/20 text-neutral-text"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
                <div>
                  <div className={`text-sm font-semibold ${isSimulatorActive ? "text-primary" : "text-neutral-text"}`}>
                    Тренажёр
                  </div>
                  <div className="text-xs text-neutral-text/60">Задачи по SQL с автопроверкой</div>
                </div>
              </Link>

              <Link
                to="/docs"
                className={`group flex items-start gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isDocsActive ? "bg-primary/10" : "hover:bg-neutral-light/80"
                }`}
                onClick={() => setPracticeOpen(false)}
              >
                <div
                  className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                    isDocsActive ? "bg-primary text-white" : "bg-neutral-light group-hover:bg-primary/20 text-neutral-text"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div>
                  <div className={`text-sm font-semibold ${isDocsActive ? "text-primary" : "text-neutral-text"}`}>
                    Схемы данных
                  </div>
                  <div className="text-xs text-neutral-text/60">Справочник по данным задач тренажёра</div>
                </div>
              </Link>
            </div>
          </div>,
          document.body
        )}

      {!isMobile &&
        toolsOpen &&
        toolsMenuPos &&
        createPortal(
          <div
            ref={toolsMenuRef}
            className="fixed w-80 bg-white border border-neutral-border rounded-xl shadow-2xl overflow-hidden"
            style={{ top: toolsMenuPos.top, left: toolsMenuPos.left, zIndex: LAYERS.headerDropdown }}
            onMouseEnter={handleToolsEnter}
            onMouseLeave={handleToolsLeave}
          >
            <div className="p-3 space-y-0.5">
              <Link
                to="/sandbox"
                className={`group flex items-start gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isSandboxActive ? "bg-primary/10" : "hover:bg-neutral-light/80"
                }`}
                onClick={() => setToolsOpen(false)}
              >
                <div
                  className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                    isSandboxActive
                      ? "bg-primary text-white"
                      : "bg-neutral-light group-hover:bg-primary/20 text-neutral-text"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <div className={`text-sm font-semibold ${isSandboxActive ? "text-primary" : "text-neutral-text"}`}>
                    Sandbox
                  </div>
                  <div className="text-xs text-neutral-text/60">Свободная среда для экспериментов с SQL</div>
                </div>
              </Link>

              <Link
                to="/ai-tutor"
                className={`group flex items-start gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isAITutorActive ? "bg-primary/10" : "hover:bg-neutral-light/80"
                }`}
                onClick={() => setToolsOpen(false)}
              >
                <div
                  className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                    isAITutorActive
                      ? "bg-primary text-white"
                      : "bg-neutral-light group-hover:bg-primary/20 text-neutral-text"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                    />
                  </svg>
                </div>
                <div>
                  <div className={`text-sm font-semibold ${isAITutorActive ? "text-primary" : "text-neutral-text"}`}>
                    AI Clickhouse Expert
                  </div>
                  <div className="text-xs text-neutral-text/60">Помощь с запросами и ClickHouse</div>
                </div>
              </Link>

              <Link
                to="/job-market"
                className={`group flex items-start gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isJobMarketActive ? "bg-primary/10" : "hover:bg-neutral-light/80"
                }`}
                onClick={() => setToolsOpen(false)}
              >
                <div
                  className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                    isJobMarketActive
                      ? "bg-primary text-white"
                      : "bg-neutral-light group-hover:bg-primary/20 text-neutral-text"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <div>
                  <div className={`text-sm font-semibold ${isJobMarketActive ? "text-primary" : "text-neutral-text"}`}>
                    Job Market
                  </div>
                  <div className="text-xs text-neutral-text/60">Рынок вакансий и стек Data Engineer</div>
                </div>
              </Link>
            </div>
          </div>,
          document.body
        )}

      {/* Mobile menu drawer */}
      {isMobile && menuOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-72 bg-white shadow-xl p-5 flex flex-col gap-4 overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-neutral-text">Меню</div>
              <button className="text-neutral-text hover:text-primary" onClick={() => setMenuOpen(false)}>✕</button>
            </div>

            <div className="space-y-3">
              {/* Курсы */}
              <Link
                to="/courses"
                className={`block px-3 py-3 rounded-lg border border-neutral-border transition-colors ${
                  isCoursesActive ? "bg-primary/10 text-primary border-primary/40" : "hover:bg-primary/10"
                }`}
                onClick={() => setMenuOpen(false)}
              >
                <div className="font-medium text-sm mb-0.5">Курсы</div>
                <div className="text-xs text-neutral-text/60">Интерактивные курсы по ClickHouse</div>
              </Link>

              {/* Практика */}
              <div className="pt-2 border-t border-neutral-border">
                <div className="text-xs font-semibold text-neutral-text/50 uppercase tracking-wider mb-2 px-1">Практика</div>
                <div className="space-y-1.5">
                  <Link
                    to="/simulator"
                    className={`block px-3 py-2.5 rounded-lg border border-neutral-border transition-colors ${
                      isSimulatorActive ? "bg-primary/10 text-primary border-primary/40" : "hover:bg-primary/10"
                    }`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <div className="font-medium text-sm mb-0.5">Тренажёр</div>
                    <div className="text-xs text-neutral-text/60">Задачи по SQL с автопроверкой</div>
                  </Link>
                  <Link
                    to="/docs"
                    className={`block px-3 py-2.5 rounded-lg border border-neutral-border transition-colors ${
                      isDocsActive ? "bg-primary/10 text-primary border-primary/40" : "hover:bg-primary/10"
                    }`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <div className="font-medium text-sm mb-0.5">Схемы данных</div>
                    <div className="text-xs text-neutral-text/60">Справочник по данным задач</div>
                  </Link>
                </div>
              </div>

              {/* Инструменты */}
              <div className="pt-2 border-t border-neutral-border">
                <div className="text-xs font-semibold text-neutral-text/50 uppercase tracking-wider mb-2 px-1">Инструменты</div>
                <div className="space-y-1.5">
                  <Link
                    to="/sandbox"
                    className={`block px-3 py-2.5 rounded-lg border border-neutral-border transition-colors ${
                      isSandboxActive ? "bg-primary/10 text-primary border-primary/40" : "hover:bg-primary/10"
                    }`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <div className="font-medium text-sm mb-0.5">Sandbox</div>
                    <div className="text-xs text-neutral-text/60">Свободная среда для SQL</div>
                  </Link>
                  <Link
                    to="/ai-tutor"
                    className={`block px-3 py-2.5 rounded-lg border border-neutral-border transition-colors ${
                      isAITutorActive ? "bg-primary/10 text-primary border-primary/40" : "hover:bg-primary/10"
                    }`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <div className="font-medium text-sm mb-0.5">AI Clickhouse Expert</div>
                    <div className="text-xs text-neutral-text/60">Помощь с запросами и ClickHouse</div>
                  </Link>
                  <Link
                    to="/job-market"
                    className={`block px-3 py-2.5 rounded-lg border border-neutral-border transition-colors ${
                      isJobMarketActive ? "bg-primary/10 text-primary border-primary/40" : "hover:bg-primary/10"
                    }`}
                    onClick={() => setMenuOpen(false)}
                  >
                    <div className="font-medium text-sm mb-0.5">Job Market</div>
                    <div className="text-xs text-neutral-text/60">Рынок вакансий и стек Data Engineer</div>
                  </Link>
                </div>
              </div>

              {/* Утилиты */}
              <div className="pt-2 border-t border-neutral-border space-y-1.5">
                <Link
                  to="/faq"
                  className={`block px-3 py-2.5 rounded-lg border border-neutral-border transition-colors ${
                    isFAQActive ? "bg-primary/10 text-primary border-primary/40" : "hover:bg-primary/10"
                  }`}
                  onClick={() => setMenuOpen(false)}
                >
                  <div className="font-medium text-sm">FAQ</div>
                </Link>
              </div>

              {/* Правовая информация */}
              <div className="pt-2 border-t border-neutral-border">
                <div className="text-xs font-semibold text-neutral-text/50 uppercase tracking-wider mb-2 px-1">Правовая</div>
                <div className="space-y-1">
                  <Link to="/privacy" className="block px-3 py-2 rounded-lg border border-neutral-border text-sm text-neutral-text/70 hover:bg-primary/10 transition-colors" onClick={() => setMenuOpen(false)}>
                    Конфиденциальность
                  </Link>
                  <Link to="/terms" className="block px-3 py-2 rounded-lg border border-neutral-border text-sm text-neutral-text/70 hover:bg-primary/10 transition-colors" onClick={() => setMenuOpen(false)}>
                    Условия использования
                  </Link>
                </div>
              </div>
            </div>

            <div className="border-t border-neutral-border pt-3 mt-auto">
              {user ? (
                <button
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition font-medium"
                  onClick={() => { navigate("/profile"); setMenuOpen(false); }}
                >
                  Профиль
                </button>
              ) : (
                <Link
                  to="/login"
                  className="block w-full text-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition font-medium"
                  onClick={() => setMenuOpen(false)}
                >
                  Войти
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
